import http from './http';

export const authApi = {
  sendCode: (phone: string, purpose = 'register') => http.post('/auth/send-code', { phone, purpose }),
  register: (data: { phone: string; code?: string; password: string; name?: string }) => http.post('/auth/register', data),
  login: (data: { phone: string; password: string }) => http.post('/auth/login', data),
  resetPassword: (data: { phone: string; code: string; newPassword: string }) => http.post('/auth/reset-password', data),
  me: () => http.get('/auth/me'),
  logout: () => http.post('/auth/logout'),
  backToOrg: () => http.post('/auth/back-to-org'),
};

export const dashboardApi = {
  summary: (type?: 'month' | 'year') => http.get('/dashboard/summary', { params: type ? { type } : {} }),
  todos: () => http.get('/dashboard/todos'),
  revenueTrend: () => http.get('/dashboard/revenue-trend'),
  taxBurdenTrend: () => http.get('/dashboard/tax-burden-trend'),
};

export const customerApi = {
  list: (params?: Record<string, unknown>) => http.get('/customers', { params }),
  summary: () => http.get('/customers/summary'),
  creators: () => http.get('/customers/creators'),
  create: (data: Record<string, unknown>) => http.post('/customers', data),
  update: (id: string, data: Record<string, unknown>) => http.patch(`/customers/${id}`, data),
  remove: (id: string) => http.delete(`/customers/${id}`),
  enter: (id: string) => http.post(`/customers/${id}/enter`),
};

export const voucherApi = {
  list: (params?: Record<string, unknown>) => http.get('/vouchers', { params }),
  accounts: () => http.get('/vouchers/accounts'),
  detail: (id: string) => http.get(`/vouchers/${id}`),
  create: (data: Record<string, unknown>) => http.post('/vouchers', data),
  update: (id: string, data: Record<string, unknown>) => http.put(`/vouchers/${id}`, data),
  remove: (id: string) => http.delete(`/vouchers/${id}`),
  audit: (id: string) => http.post(`/vouchers/${id}/audit`),
  copy: (id: string) => http.post(`/vouchers/${id}/copy`),
  reverse: (id: string) => http.post(`/vouchers/${id}/reverse`),
  batchCopy: (ids: string[]) => http.post('/vouchers/batch-copy', { ids }),
  batchRemove: (ids: string[]) => http.post('/vouchers/batch-remove', { ids }),
  tidyPreview: (method: string) => http.get('/vouchers/tidy-preview', { params: { method } }),
  tidyApply: (method: string) => http.post('/vouchers/tidy-apply', { method }),
};

export const invoiceApi = {
  list: (params?: Record<string, unknown>) => http.get('/invoices', { params }),
  detail: (id: string) => http.get(`/invoices/${id}`),
  create: (data: Record<string, unknown>) => http.post('/invoices', data),
  update: (id: string, data: Record<string, unknown>) => http.patch(`/invoices/${id}`, data),
  verification: (id: string, verificationStatus: string) => http.patch(`/invoices/${id}/verification`, { verificationStatus }),
  remove: (id: string) => http.delete(`/invoices/${id}`),
  generateVoucher: (period: string) => http.post('/invoices/generate-voucher', { period }),
  fetchProvider: (period: string) => http.post('/invoices/fetch', { period }),
  generateForInvoice: (id: string) => http.post(`/invoices/${id}/generate-voucher`),
  fundOverview: (period?: string) => http.get('/invoices/fund-overview', { params: period ? { period } : {} }),
  riskAnalysis: () => http.get('/invoices/risk-analysis'),
};

export const taxDeclarationApi = {
  list: (period?: string) => http.get('/tax-declarations', { params: period ? { period } : {} }),
  calendar: () => http.get('/tax-declarations/calendar'),
  generate: (data: Record<string, unknown>) => http.post('/tax-declarations/generate', data),
  update: (id: string, data: Record<string, unknown>) => http.patch(`/tax-declarations/${id}`, data),
  submit: (id: string) => http.post(`/tax-declarations/${id}/submit`),
  oneClick: (period: string) => http.post('/tax-declarations/one-click', { period }),
  remove: (id: string) => http.delete(`/tax-declarations/${id}`),
  void: (id: string) => http.post(`/tax-declarations/${id}/void`),
  correct: (id: string) => http.post(`/tax-declarations/${id}/correct`),
  pay: (id: string, paymentDate?: string) => http.post(`/tax-declarations/${id}/pay`, { paymentDate }),
  listTaxTypes: () => http.get('/tax-declarations/tax-types'),
  createTaxType: (data: Record<string, unknown>) => http.post('/tax-declarations/tax-types', data),
  updateTaxType: (id: string, data: Record<string, unknown>) => http.patch(`/tax-declarations/tax-types/${id}`, data),
  removeTaxType: (id: string) => http.delete(`/tax-declarations/tax-types/${id}`),
  invoiceSummary: (period: string) => http.get('/tax-declarations/invoice-summary', { params: { period } }),
  taxReport: (period: string) => http.get('/tax-declarations/tax-report', { params: { period } }),
  payrollTax: (period: string) => http.get('/tax-declarations/payroll-tax', { params: { period } }),
  tasks: () => http.get('/tax-declarations/tasks'),
};

export const fixedAssetApi = {
  list: () => http.get('/fixed-assets'),
  detail: (id: string) => http.get(`/fixed-assets/${id}`),
  create: (data: Record<string, unknown>) => http.post('/fixed-assets', data),
  depreciate: () => http.post('/fixed-assets/depreciate'),
  change: (id: string, action: string) => http.post(`/fixed-assets/${id}/change`, { action }),
  remove: (id: string) => http.delete(`/fixed-assets/${id}`),
  update: (id: string, data: Record<string, unknown>) => http.patch(`/fixed-assets/${id}`, data),
  generateDepreciationVoucher: (period: string) => http.post('/fixed-assets/generate-depreciation-voucher', { period }),
  impair: (id: string, amount: number) => http.post(`/fixed-assets/${id}/impair`, { amount }),
  dispose: (id: string) => http.post(`/fixed-assets/${id}/dispose`),
};

export const receivablePayableApi = {
  list: (type?: string) => http.get('/receivables-payables', { params: type ? { type } : {} }),
  agingAnalysis: () => http.get('/receivables-payables/aging-analysis'),
  detail: (id: string) => http.get(`/receivables-payables/${id}`),
  create: (data: Record<string, unknown>) => http.post('/receivables-payables', data),
  payment: (id: string, data: Record<string, unknown>) => http.post(`/receivables-payables/${id}/payment`, data),
  remove: (id: string) => http.delete(`/receivables-payables/${id}`),
};

export const financialStatementApi = {
  report: (period: string, reclass = true) => http.get('/financial-statements/report', { params: { period, reclass: reclass ? '1' : '0' } }),
  balanceSheet: () => http.get('/financial-statements/balance-sheet'),
  incomeStatement: () => http.get('/financial-statements/income-statement'),
  cashFlow: () => http.get('/financial-statements/cash-flow'),
  export: () => http.get('/financial-statements/export'),
};

export const ledgerApi = {
  trialBalance: (period: string, from?: string, to?: string) => http.get('/ledger/trial-balance', { params: { period, from: from || undefined, to: to || undefined } }),
  generalLedger: (period: string, from?: string, to?: string) => http.get('/ledger/general-ledger', { params: { period, from: from || undefined, to: to || undefined } }),
  accountDetail: (accountCode: string, period: string, from?: string, to?: string) => http.get('/ledger/account-detail', { params: { accountCode, period, from: from || undefined, to: to || undefined } }),
  trialBalanceSummary: (period: string) => http.get('/ledger/trial-balance-summary', { params: { period } }),
  voucherSummary: (period: string) => http.get('/ledger/voucher-summary', { params: { period } }),
  multiColumn: (accountCode: string, period: string, from?: string, to?: string) => http.get('/ledger/multi-column', { params: { accountCode, period, from: from || undefined, to: to || undefined } }),
  quantityAmount: (accountCode: string, period: string, from?: string, to?: string) => http.get('/ledger/quantity-amount', { params: { accountCode, period, from: from || undefined, to: to || undefined } }),
  auxiliary: (period: string) => http.get('/ledger/auxiliary', { params: { period } }),
};

export const periodCloseApi = {
  carryForward: (period: string) => http.post('/period-close/carry-forward', { period }),
  accrueTax: (period: string) => http.post('/period-close/accrue-tax', { period }),
  carryForwardCost: (period: string) => http.post('/period-close/carry-forward-cost', { period }),
  close: (period: string) => http.post('/period-close/close', { period }),
  reopen: (period: string) => http.post('/period-close/reopen', { period }),
};

export const settingsApi = {
  openingList: () => http.get('/settings/opening-balances'),
  saveOpening: (items: Record<string, unknown>[]) => http.put('/settings/opening-balances', { items }),
  invoiceProvider: () => http.get('/settings/invoice-provider'),
  saveInvoiceProvider: (data: Record<string, unknown>) => http.put('/settings/invoice-provider', data),
  shortDomains: () => http.get('/settings/short-domains'),
  saveShortDomains: (domains: string[]) => http.put('/settings/short-domains', { domains }),
  xhsImage: () => http.get('/settings/xhs-image'),
  saveXhsImage: (data: Record<string, unknown>) => http.put('/settings/xhs-image', data),
};

export const xhsApi = {
  generate: (topic: string, count: number, excludeTitles: string[], fresh = false, extra = '') =>
    http.post('/certificates/xhs/generate', { topic, count, excludeTitles, fresh, extra }, { timeout: 180000 }),
  notes: (params?: Record<string, unknown>) => http.get('/certificates/xhs/notes', { params }),
  detail: (id: string) => http.get(`/certificates/xhs/notes/${id}`),
  saveCover: (id: string, data: Record<string, unknown>) => http.patch(`/certificates/xhs/notes/${id}/cover`, data),
  remove: (id: string) => http.delete(`/certificates/xhs/notes/${id}`),
  makeImage: (prompt: string, promptEn?: string) => http.post('/certificates/xhs/make-image', { prompt, promptEn }, { timeout: 300000 }),
};

export const payrollApi = {
  listEmployees: () => http.get('/payroll/employees'),
  createEmployee: (data: Record<string, unknown>) => http.post('/payroll/employees', data),
  updateEmployee: (id: string, data: Record<string, unknown>) => http.patch(`/payroll/employees/${id}`, data),
  removeEmployee: (id: string) => http.delete(`/payroll/employees/${id}`),
  list: (period?: string) => http.get('/payroll', { params: period ? { period } : {} }),
  create: (data: Record<string, unknown>) => http.post('/payroll', data),
  removePayroll: (id: string) => http.delete(`/payroll/${id}`),
  generateVoucher: (period: string) => http.post('/payroll/generate-voucher', { period }),
};

export const bankApi = {
  list: (period?: string) => http.get('/bank/transactions', { params: period ? { period } : {} }),
  create: (data: Record<string, unknown>) => http.post('/bank/transactions', data),
  importMany: (items: any[]) => http.post('/bank/transactions/import', { items }),
  reconcile: (period: string) => http.post('/bank/reconcile', { period }),
  remove: (id: string) => http.delete(`/bank/transactions/${id}`),
  generateVoucher: (period: string) => http.post('/bank/generate-voucher', { period }),
};

export const inventoryApi = {
  list: () => http.get('/inventory'),
  createItem: (data: Record<string, unknown>) => http.post('/inventory/items', data),
  movement: (data: Record<string, unknown>) => http.post('/inventory/movements', data),
  movements: (itemId?: string) => http.get('/inventory/movements', { params: itemId ? { itemId } : {} }),
  removeItem: (id: string) => http.delete(`/inventory/items/${id}`),
};

export const invoiceOcrApi = {
  recognize: (image: string) => http.post('/invoice-ocr/recognize', { image }),
};

export const voucherTemplateApi = {
  list: () => http.get('/voucher-templates'),
  create: (data: Record<string, unknown>) => http.post('/voucher-templates', data),
  generate: (id: string, data: Record<string, unknown>) => http.post(`/voucher-templates/${id}/generate`, data),
  remove: (id: string) => http.delete(`/voucher-templates/${id}`),
};

export const expenseApi = {
  list: () => http.get('/expenses'),
  create: (data: Record<string, unknown>) => http.post('/expenses', data),
  remove: (id: string) => http.delete(`/expenses/${id}`),
};

export const userApi = {
  list: () => http.get('/users'),
  create: (data: Record<string, unknown>) => http.post('/users', data),
  update: (id: string, data: Record<string, unknown>) => http.patch(`/users/${id}`, data),
  remove: (id: string) => http.delete(`/users/${id}`),
  updateRole: (id: string, role: string) => http.patch(`/users/${id}/role`, { role }),
};

export const accountSetApi = {
  list: (customerId?: string) => http.get('/account-sets', { params: customerId ? { customerId } : {} }),
  create: (data: Record<string, unknown>) => http.post('/account-sets', data),
  update: (id: string, data: Record<string, unknown>) => http.patch(`/account-sets/${id}`, data),
  remove: (id: string) => http.delete(`/account-sets/${id}`),
};

export const aiApi = {
  chat: (message: string, search?: boolean, image?: string) => http.post('/ai/chat', { message, search, image }),
};

export const shortLinkApi = {
  list: (params?: Record<string, unknown>) => http.get('/short-links', { params }),
  create: (data: Record<string, unknown>) => http.post('/short-links', data),
  update: (id: string, data: Record<string, unknown>) => http.patch(`/short-links/${id}`, data),
  remove: (id: string) => http.delete(`/short-links/${id}`),
  batch: (text: string) => http.post('/short-links/batch', { text }),
  stats: (id: string) => http.get(`/short-links/${id}/stats`),
  listGroups: () => http.get('/short-groups'),
  createGroup: (name: string) => http.post('/short-groups', { name }),
  removeGroup: (id: string) => http.delete(`/short-groups/${id}`),
};

export const certificateApi = {
  list: (params?: Record<string, unknown>) => http.get('/certificates', { params }),
  summary: () => http.get('/certificates/summary'),
  detail: (id: string) => http.get(`/certificates/${id}`),
  create: (data: Record<string, unknown>) => http.post('/certificates', data),
  update: (id: string, data: Record<string, unknown>) => http.patch(`/certificates/${id}`, data),
  remove: (id: string) => http.delete(`/certificates/${id}`),
};
